// Tutor SessionDetailPage — session info, attendance (with check-in),
// feedback, and materials for one session, all on a single page.
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth.js";
import { useFetch, postJson } from "../../hooks/useApi.js";
import { useSessions } from "../../contexts/SessionsContext.js";
import Badge from "../../components/shared/Badge.js";
import SectionCard from "../../components/shared/SectionCard.js";
import { IconArrowLeft, IconCalendarEvent, IconFiles, IconMessage, IconUsers } from "../../components/shared/icons.js";
import type {
  AttendanceRecordWithStudent,
  EnrollmentWithStudent,
  FeedbackRecord,
  Material,
  PaginatedResult,
} from "@dm-lms/shared/types";

interface FeedbackEditState {
  notes: string;
  isPrivate: boolean;
  saving: boolean;
  saved: boolean;
  error: string;
}

function formatDelivered(iso: string): string {
  return `Delivered ${new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}`;
}

export default function SessionDetailPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const { user } = useAuth();

  const { sessions: sessionsData, isLoading: sessionsLoading } = useSessions();
  const session = useMemo(
    () => sessionsData.find((s) => s.id === sessionId),
    [sessionsData, sessionId]
  );
  const courseId = session?.courseId ?? null;

  const { data: enrollmentsData } = useFetch<PaginatedResult<EnrollmentWithStudent>>(
    courseId ? `/api/enrollments/course/${courseId}?page=1&pageSize=100` : null
  );
  const enrollments = enrollmentsData?.items ?? [];

  const { data: attendanceData, refetch: refetchAttendance } = useFetch<AttendanceRecordWithStudent[]>(
    sessionId ? `/api/attendance/session/${sessionId}` : null
  );

  const { data: feedbackData } = useFetch<FeedbackRecord[]>(
    sessionId ? `/api/feedback?sessionId=${sessionId}` : null
  );

  const { data: materials, isLoading: materialsLoading } = useFetch<Material[]>(
    courseId ? `/api/materials?courseId=${courseId}` : null
  );

  // ---- Attendance: merge enrolled students with their attendance record ----
  const attendanceByStudent = useMemo(() => {
    const map = new Map<string, AttendanceRecordWithStudent>();
    for (const record of attendanceData ?? []) map.set(record.studentId, record);
    return map;
  }, [attendanceData]);

  const [checkingInId, setCheckingInId] = useState<string | null>(null);

  async function checkIn(studentId: string) {
    if (!sessionId) return;
    setCheckingInId(studentId);
    await postJson("/api/attendance/check-in", { studentId, sessionId });
    setCheckingInId(null);
    refetchAttendance();
  }

  // ---- Feedback: local edit state, seeded once enrollments + feedback have loaded ----
  const [feedbackState, setFeedbackState] = useState<Record<string, FeedbackEditState>>({});

  useEffect(() => {
    if (enrollments.length === 0 || feedbackData === null) return;
    setFeedbackState((prev) => {
      const next = { ...prev };
      for (const enrollment of enrollments) {
        if (next[enrollment.studentId]) continue;
        const existing = feedbackData.find((f) => f.studentId === enrollment.studentId);
        next[enrollment.studentId] = {
          notes: existing?.notes ?? "",
          isPrivate: existing?.isPrivate ?? true,
          saving: false,
          saved: false,
          error: "",
        };
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enrollments.length, feedbackData]);

  function updateFeedback(studentId: string, patch: Partial<FeedbackEditState>) {
    setFeedbackState((prev) => {
      const current = prev[studentId];
      if (!current) return prev;
      const merged: FeedbackEditState = {
        notes: patch.notes ?? current.notes,
        isPrivate: patch.isPrivate ?? current.isPrivate,
        saving: patch.saving ?? current.saving,
        saved: false,
        error: patch.error ?? current.error,
      };
      return { ...prev, [studentId]: merged };
    });
  }

  async function saveFeedback(studentId: string) {
    if (!session || !user) return;
    const entry = feedbackState[studentId];
    if (!entry) return;

    setFeedbackState((prev) => {
      const current = prev[studentId];
      if (!current) return prev;
      return { ...prev, [studentId]: { ...current, saving: true, error: "" } };
    });

    const result = await postJson<FeedbackRecord>("/api/feedback", {
      tutorId: user.id,
      studentId,
      sessionId: session.id,
      notes: entry.notes,
      isPrivate: entry.isPrivate,
    });

    if (!result.ok) {
      setFeedbackState((prev) => {
        const current = prev[studentId];
        if (!current) return prev;
        return { ...prev, [studentId]: { ...current, saving: false, error: result.error } };
      });
      return;
    }

    setFeedbackState((prev) => {
      const current = prev[studentId];
      if (!current) return prev;
      return { ...prev, [studentId]: { ...current, saving: false, saved: true } };
    });
  }

  const isPast = session ? new Date(session.scheduledAt).getTime() < Date.now() : false;

  return (
    <div className="dm-page">
      <Link
        to="/app/tutor/sessions"
        className="inline-flex items-center gap-1 text-dm-gold hover:underline mb-4"
        style={{ fontSize: "12px" }}
      >
        <IconArrowLeft width={12} height={12} />
        All sessions
      </Link>

      {sessionsLoading && <p className="text-dm-slate text-sm">Loading session…</p>}
      {!sessionsLoading && !session && <p className="text-red-600 text-sm">Session not found.</p>}

      {session && (
        <>
          <div className="mb-6">
            <h1 className="text-2xl font-semibold text-dm-navy">{session.title}</h1>
            <div className="flex items-center gap-2 mt-2">
              <p className="text-dm-slate text-sm">{session.courseTitle}</p>
              <Badge tone="gray">{session.mode}</Badge>
              <Badge tone={isPast ? "gray" : "green"}>{isPast ? "Past" : "Upcoming"}</Badge>
            </div>
          </div>

          <div className="space-y-6">
            <SectionCard title="Session info" icon={<IconCalendarEvent />}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-xs text-dm-slate mb-1">Date and time</p>
                  <p className="text-dm-navy">{new Date(session.scheduledAt).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-xs text-dm-slate mb-1">Duration</p>
                  <p className="text-dm-navy">{session.durationMinutes} min</p>
                </div>
                <div>
                  <p className="text-xs text-dm-slate mb-1">Venue</p>
                  <p className="text-dm-navy">{session.venue ?? "—"}</p>
                </div>
                <div>
                  <p className="text-xs text-dm-slate mb-1">Meeting link</p>
                  {session.meetingUrl ? (
                    <a
                      href={session.meetingUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-dm-blue hover:underline break-all"
                    >
                      {session.meetingUrl}
                    </a>
                  ) : (
                    <p className="text-dm-navy">—</p>
                  )}
                </div>
              </div>
            </SectionCard>

            <SectionCard title="Attendance" icon={<IconUsers />}>
              {enrollments.length === 0 ? (
                <p className="text-dm-slate text-sm">No students enrolled in this course yet.</p>
              ) : (
                <div className="overflow-x-auto rounded-lg border border-gray-200">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 text-left text-dm-slate">
                      <tr>
                        <th className="px-4 py-3 font-medium">Student</th>
                        <th className="px-4 py-3 font-medium">Status</th>
                        <th className="px-4 py-3 font-medium">Checked in</th>
                        <th className="px-4 py-3 font-medium" />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {enrollments.map((enrollment) => {
                        const record = attendanceByStudent.get(enrollment.studentId);
                        const status = record?.status ?? "absent";
                        const isChecking = checkingInId === enrollment.studentId;
                        return (
                          <tr key={enrollment.studentId}>
                            <td className="px-4 py-3">
                              {enrollment.studentName}{" "}
                              <span className="text-dm-slate">({enrollment.studentCode})</span>
                            </td>
                            <td className="px-4 py-3">
                              <Badge tone={status === "present" ? "green" : status === "excused" ? "gold" : "red"}>
                                {status}
                              </Badge>
                            </td>
                            <td className="px-4 py-3">
                              {record?.checkedInAt ? new Date(record.checkedInAt).toLocaleString() : "—"}
                            </td>
                            <td className="px-4 py-3">
                              {status === "present" ? (
                                <span className="text-xs text-green-700 font-medium">✓ Checked in</span>
                              ) : (
                                <button
                                  type="button"
                                  className="text-xs px-3 py-1.5 rounded-md bg-dm-blue text-white disabled:opacity-50"
                                  disabled={isChecking}
                                  onClick={() => checkIn(enrollment.studentId)}
                                >
                                  {isChecking ? "Checking in…" : "Check in"}
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </SectionCard>

            <SectionCard title="Feedback" icon={<IconMessage />}>
              {enrollments.length === 0 ? (
                <p className="text-dm-slate text-sm">No students enrolled in this course yet.</p>
              ) : (
                <div className="space-y-4">
                  {enrollments.map((enrollment) => {
                    const entry = feedbackState[enrollment.studentId];
                    if (!entry) return null;
                    return (
                      <div key={enrollment.studentId} className="border border-gray-100 rounded-lg p-3">
                        <p className="text-sm font-medium text-dm-navy mb-2">
                          {enrollment.studentName}{" "}
                          <span className="text-dm-slate font-normal">({enrollment.studentCode})</span>
                        </p>
                        <textarea
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm mb-2"
                          rows={3}
                          value={entry.notes}
                          onChange={(e) => updateFeedback(enrollment.studentId, { notes: e.target.value })}
                        />
                        <div className="flex items-center justify-between">
                          <label className="flex items-center gap-2 text-xs text-dm-slate">
                            <input
                              type="checkbox"
                              checked={entry.isPrivate}
                              onChange={(e) => updateFeedback(enrollment.studentId, { isPrivate: e.target.checked })}
                            />
                            Private
                          </label>
                          <div className="flex items-center gap-3">
                            {entry.saved && <span className="text-xs text-green-700">Saved</span>}
                            {entry.error && <span className="text-xs text-red-600">{entry.error}</span>}
                            <button
                              type="button"
                              className="text-xs px-3 py-1.5 rounded-md bg-dm-blue text-white disabled:opacity-50"
                              disabled={entry.saving}
                              onClick={() => saveFeedback(enrollment.studentId)}
                            >
                              {entry.saving ? "Saving…" : "Save feedback"}
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </SectionCard>

            <SectionCard title="Materials" icon={<IconFiles />}>
              {materialsLoading && <p className="text-dm-slate text-sm">Loading materials…</p>}
              {!materialsLoading && (materials ?? []).length === 0 ? (
                <p className="text-dm-slate text-sm">No materials uploaded for this course yet.</p>
              ) : (
                <ul className="divide-y divide-gray-100">
                  {(materials ?? []).map((material) => (
                    <li key={material.id} className="py-3 flex items-center justify-between">
                      <div>
                        <p className="text-sm text-dm-navy">{material.title}</p>
                        <p className="text-xs text-dm-slate">{formatDelivered(material.deliverAt)}</p>
                      </div>
                      <button
                        type="button"
                        disabled
                        title="Coming soon"
                        className="text-xs px-3 py-1.5 rounded-md border border-gray-300 text-dm-slate disabled:opacity-50 cursor-not-allowed"
                      >
                        Download
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </SectionCard>
          </div>
        </>
      )}
    </div>
  );
}
